import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Shield, 
  Lock, 
  Eye, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  FileText, 
  Key, 
  Database, 
  Network, 
  Users, 
  Clock,
  Scan,
  FileCheck,
  ShieldCheck,
  AlertCircle,
  Download,
  RefreshCw
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface SecurityAlert {
  id: string;
  type: 'vulnerability' | 'breach_attempt' | 'suspicious_activity' | 'policy_violation' | 'access_anomaly';
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  source: string;
  timestamp: Date;
  status: 'active' | 'investigating' | 'resolved' | 'false_positive';
  metadata?: any;
}

interface ComplianceFramework {
  id: string;
  name: string;
  description: string;
  requirements: ComplianceRequirement[];
  status: 'compliant' | 'partial' | 'non_compliant' | 'pending';
  lastAudit: Date;
  nextAudit: Date;
  score: number;
}

interface ComplianceRequirement {
  id: string;
  title: string;
  description: string;
  category: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  status: 'compliant' | 'non_compliant' | 'pending' | 'not_applicable';
  evidence: string[];
  lastChecked: Date;
}

interface AuditLog {
  id: string;
  userId: string;
  userName: string;
  action: string;
  resource: string;
  details: string;
  timestamp: Date;
  ipAddress: string;
  userAgent: string;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
}

interface SecurityMetrics {
  totalAlerts: number;
  criticalAlerts: number;
  resolvedAlerts: number;
  averageResolutionTime: number;
  complianceScore: number;
  vulnerabilities: number;
  lastScan: Date;
  dataEncryptionLevel: number;
}

export const ProductionSecurityCompliance: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  // State management
  const [securityAlerts, setSecurityAlerts] = useState<SecurityAlert[]>([]);
  const [complianceFrameworks, setComplianceFrameworks] = useState<ComplianceFramework[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [securityMetrics, setSecurityMetrics] = useState<SecurityMetrics>({
    totalAlerts: 0,
    criticalAlerts: 0,
    resolvedAlerts: 0,
    averageResolutionTime: 0,
    complianceScore: 0,
    vulnerabilities: 0,
    lastScan: new Date(),
    dataEncryptionLevel: 100
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFramework, setSelectedFramework] = useState<string>('');
  const [scanInProgress, setScanInProgress] = useState(false);

  // Initialize security monitoring
  useEffect(() => {
    if (!user?.id) return;
    
    loadSecurityData();
    startSecurityMonitoring();
  }, [user?.id]);

  const loadSecurityData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        loadSecurityAlerts(),
        loadComplianceFrameworks(),
        loadAuditLogs(),
        loadSecurityMetrics()
      ]);
    } catch (error) {
      console.error('Failed to load security data:', error);
      toast({
        title: "Error",
        description: "Failed to load security data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadSecurityAlerts = async () => {
    // Simulate security alerts data
    const mockAlerts: SecurityAlert[] = [
      {
        id: '1',
        type: 'suspicious_activity',
        severity: 'high',
        title: 'Multiple Failed Login Attempts',
        description: 'Detected 15 failed login attempts from IP 192.168.1.100 in the last 10 minutes',
        source: 'Authentication Service',
        timestamp: new Date(Date.now() - 300000),
        status: 'active'
      },
      {
        id: '2',
        type: 'vulnerability',
        severity: 'medium',
        title: 'Outdated SSL Certificate',
        description: 'SSL certificate for subdomain api.example.com expires in 14 days',
        source: 'Certificate Monitor',
        timestamp: new Date(Date.now() - 3600000),
        status: 'investigating'
      },
      {
        id: '3',
        type: 'policy_violation',
        severity: 'low',
        title: 'Password Policy Violation',
        description: 'User attempted to set a weak password not meeting complexity requirements',
        source: 'Password Policy Engine',
        timestamp: new Date(Date.now() - 7200000),
        status: 'resolved'
      }
    ];
    setSecurityAlerts(mockAlerts);
  };

  const loadComplianceFrameworks = async () => {
    // Simulate compliance frameworks data
    const mockFrameworks: ComplianceFramework[] = [
      {
        id: 'gdpr',
        name: 'GDPR',
        description: 'General Data Protection Regulation',
        status: 'compliant',
        lastAudit: new Date(Date.now() - 86400000 * 30),
        nextAudit: new Date(Date.now() + 86400000 * 335),
        score: 92,
        requirements: [
          {
            id: 'gdpr-1',
            title: 'Data Processing Lawfulness',
            description: 'Ensure all data processing has a lawful basis',
            category: 'Data Protection',
            priority: 'critical',
            status: 'compliant',
            evidence: ['privacy-policy.pdf', 'consent-forms.json'],
            lastChecked: new Date()
          },
          {
            id: 'gdpr-2',
            title: 'Right to be Forgotten',
            description: 'Implement data deletion capabilities',
            category: 'Individual Rights',
            priority: 'high',
            status: 'compliant',
            evidence: ['deletion-procedure.md', 'test-results.json'],
            lastChecked: new Date()
          }
        ]
      },
      {
        id: 'sox',
        name: 'SOX',
        description: 'Sarbanes-Oxley Act',
        status: 'partial',
        lastAudit: new Date(Date.now() - 86400000 * 60),
        nextAudit: new Date(Date.now() + 86400000 * 305),
        score: 78,
        requirements: [
          {
            id: 'sox-1',
            title: 'Internal Controls',
            description: 'Maintain adequate internal financial controls',
            category: 'Financial Controls',
            priority: 'critical',
            status: 'pending',
            evidence: [],
            lastChecked: new Date(Date.now() - 86400000 * 7)
          }
        ]
      }
    ];
    setComplianceFrameworks(mockFrameworks);
  };

  const loadAuditLogs = async () => {
    // Simulate audit logs data
    const mockLogs: AuditLog[] = [
      {
        id: '1',
        userId: user?.id || '',
        userName: 'John Doe',
        action: 'LOGIN',
        resource: 'Authentication',
        details: 'Successful login from Chrome browser',
        timestamp: new Date(Date.now() - 300000),
        ipAddress: '192.168.1.50',
        userAgent: 'Mozilla/5.0 Chrome/91.0',
        risk_level: 'low'
      },
      {
        id: '2',
        userId: user?.id || '',
        userName: 'Jane Smith',
        action: 'DATA_EXPORT',
        resource: 'Bills',
        details: 'Exported 150 bill records to CSV',
        timestamp: new Date(Date.now() - 600000),
        ipAddress: '192.168.1.75',
        userAgent: 'Mozilla/5.0 Firefox/89.0',
        risk_level: 'medium'
      }
    ];
    setAuditLogs(mockLogs);
  };

  const loadSecurityMetrics = async () => {
    // Calculate metrics from alerts
    const totalAlerts = securityAlerts.length;
    const criticalAlerts = securityAlerts.filter(a => a.severity === 'critical').length;
    const resolvedAlerts = securityAlerts.filter(a => a.status === 'resolved').length;
    
    setSecurityMetrics({
      totalAlerts,
      criticalAlerts,
      resolvedAlerts,
      averageResolutionTime: 2.5,
      complianceScore: 85,
      vulnerabilities: 3,
      lastScan: new Date(Date.now() - 3600000),
      dataEncryptionLevel: 100
    });
  };

  const startSecurityMonitoring = () => {
    // Set up real-time monitoring
    const interval = setInterval(() => {
      // Simulate new security events
      if (Math.random() > 0.95) {
        generateSecurityAlert();
      }
    }, 10000);

    return () => clearInterval(interval);
  };

  const generateSecurityAlert = () => {
    const alertTypes = ['suspicious_activity', 'policy_violation', 'access_anomaly'];
    const severities = ['low', 'medium', 'high'];
    
    const newAlert: SecurityAlert = {
      id: Date.now().toString(),
      type: alertTypes[Math.floor(Math.random() * alertTypes.length)] as any,
      severity: severities[Math.floor(Math.random() * severities.length)] as any,
      title: 'New Security Event Detected',
      description: 'Automated security monitoring detected a potential issue',
      source: 'Security Monitor',
      timestamp: new Date(),
      status: 'active'
    };

    setSecurityAlerts(prev => [newAlert, ...prev].slice(0, 50));
    
    if (newAlert.severity === 'high' || newAlert.severity === 'critical') {
      toast({
        title: "Security Alert",
        description: newAlert.title,
        variant: "destructive"
      });
    }
  };

  const runSecurityScan = async () => {
    setScanInProgress(true);
    
    try {
      // Simulate security scan
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Generate scan results
      const vulnerabilitiesFound = Math.floor(Math.random() * 5);
      
      setSecurityMetrics(prev => ({
        ...prev,
        vulnerabilities: vulnerabilitiesFound,
        lastScan: new Date()
      }));

      toast({
        title: "Security Scan Complete",
        description: `Found ${vulnerabilitiesFound} vulnerabilities`,
        variant: vulnerabilitiesFound > 0 ? "destructive" : "default"
      });
    } catch (error) {
      toast({
        title: "Scan Failed",
        description: "Security scan could not be completed",
        variant: "destructive"
      });
    } finally {
      setScanInProgress(false);
    }
  };

  const resolveAlert = async (alertId: string) => {
    setSecurityAlerts(prev => 
      prev.map(alert => 
        alert.id === alertId 
          ? { ...alert, status: 'resolved' }
          : alert
      )
    );

    toast({
      title: "Alert Resolved",
      description: "Security alert has been marked as resolved"
    });
  };

  const exportComplianceReport = async (frameworkId: string) => {
    try {
      const framework = complianceFrameworks.find(f => f.id === frameworkId);
      if (!framework) return;

      // Simulate report generation
      const reportData = {
        framework: framework.name,
        score: framework.score,
        status: framework.status,
        requirements: framework.requirements,
        generatedAt: new Date(),
        generatedBy: user?.email
      };

      // Convert to JSON and download
      const blob = new Blob([JSON.stringify(reportData, null, 2)], 
        { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${framework.name}-compliance-report.json`;
      a.click();

      toast({
        title: "Report Exported",
        description: `${framework.name} compliance report downloaded`
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Could not export compliance report",
        variant: "destructive"
      });
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'non_compliant': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'partial': return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'pending': return <Clock className="h-4 w-4 text-blue-500" />;
      default: return <AlertTriangle className="h-4 w-4 text-gray-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="animate-pulse space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-8 bg-muted rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Production Security & Compliance</h2>
          <p className="text-muted-foreground">
            Monitor security threats, ensure compliance, and maintain audit trails
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            onClick={runSecurityScan}
            disabled={scanInProgress}
            variant="outline"
            className="flex items-center gap-2"
          >
            {scanInProgress ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Scan className="h-4 w-4" />
            )}
            {scanInProgress ? 'Scanning...' : 'Run Security Scan'}
          </Button>
        </div>
      </div>

      {/* Security Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Security Alerts</p>
                <p className="text-2xl font-bold">{securityMetrics.totalAlerts}</p>
                <p className="text-xs text-red-600">{securityMetrics.criticalAlerts} critical</p>
              </div>
              <Shield className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Compliance Score</p>
                <p className="text-2xl font-bold">{securityMetrics.complianceScore}%</p>
                <Progress value={securityMetrics.complianceScore} className="h-2 mt-1" />
              </div>
              <FileCheck className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Vulnerabilities</p>
                <p className="text-2xl font-bold">{securityMetrics.vulnerabilities}</p>
                <p className="text-xs text-muted-foreground">
                  Last scan: {securityMetrics.lastScan.toLocaleTimeString()}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Data Encryption</p>
                <p className="text-2xl font-bold">{securityMetrics.dataEncryptionLevel}%</p>
                <p className="text-xs text-green-600">All data encrypted</p>
              </div>
              <Lock className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="alerts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="alerts">Security Alerts</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="audit">Audit Logs</TabsTrigger>
          <TabsTrigger value="monitoring">Monitoring</TabsTrigger>
        </TabsList>

        {/* Security Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Active Security Alerts</CardTitle>
              <CardDescription>
                Real-time security threats and policy violations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-96">
                <div className="space-y-3">
                  {securityAlerts.map((alert) => (
                    <div
                      key={alert.id}
                      className="flex items-start gap-3 p-4 rounded-lg border bg-background"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        <Shield className="h-5 w-5 text-red-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium">{alert.title}</h4>
                          <div className="flex items-center gap-2">
                            <Badge variant={getSeverityColor(alert.severity)}>
                              {alert.severity}
                            </Badge>
                            <Badge variant="outline">
                              {alert.status}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {alert.description}
                        </p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{alert.source}</span>
                          <span>{alert.timestamp.toLocaleString()}</span>
                        </div>
                        {alert.status === 'active' && (
                          <div className="mt-3">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => resolveAlert(alert.id)}
                            >
                              Mark as Resolved
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {securityAlerts.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No active security alerts</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="space-y-4">
          <div className="flex items-center gap-4 mb-4">
            <Select value={selectedFramework} onValueChange={setSelectedFramework}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select framework" />
              </SelectTrigger>
              <SelectContent>
                {complianceFrameworks.map((framework) => (
                  <SelectItem key={framework.id} value={framework.id}>
                    {framework.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedFramework && (
              <Button
                variant="outline"
                onClick={() => exportComplianceReport(selectedFramework)}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Export Report
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {complianceFrameworks.map((framework) => (
              <Card key={framework.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      {getStatusIcon(framework.status)}
                      {framework.name}
                    </CardTitle>
                    <Badge variant="outline">
                      {framework.score}% compliant
                    </Badge>
                  </div>
                  <CardDescription>{framework.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Last Audit:</span>
                      <p>{framework.lastAudit.toLocaleDateString()}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Next Audit:</span>
                      <p>{framework.nextAudit.toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  <Progress value={framework.score} className="h-2" />
                  
                  <div className="space-y-2">
                    <h5 className="font-medium">Key Requirements</h5>
                    {framework.requirements.slice(0, 3).map((req) => (
                      <div key={req.id} className="flex items-center justify-between text-sm">
                        <span className="truncate">{req.title}</span>
                        <Badge variant={req.status === 'compliant' ? 'default' : 'secondary'}>
                          {req.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Audit Trail</CardTitle>
              <CardDescription>
                Complete log of user actions and system events
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-96">
                <div className="space-y-2">
                  {auditLogs.map((log) => (
                    <div
                      key={log.id}
                      className="flex items-center gap-3 p-3 rounded-lg border bg-background"
                    >
                      <div className="flex-shrink-0">
                        <Users className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{log.userName}</span>
                            <Badge variant="outline">{log.action}</Badge>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Badge variant={getSeverityColor(log.risk_level)}>
                              {log.risk_level}
                            </Badge>
                            <span>{log.timestamp.toLocaleString()}</span>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {log.details} • {log.resource}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          IP: {log.ipAddress}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monitoring Tab */}
        <TabsContent value="monitoring" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Security Monitoring Status</CardTitle>
                <CardDescription>
                  Real-time security monitoring systems
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Intrusion Detection
                    </span>
                    <Badge variant="default">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Network className="h-4 w-4" />
                      Network Monitoring
                    </span>
                    <Badge variant="default">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Database className="h-4 w-4" />
                      Database Activity
                    </span>
                    <Badge variant="default">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Key className="h-4 w-4" />
                      Access Control
                    </span>
                    <Badge variant="default">Active</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Data Protection Status</CardTitle>
                <CardDescription>
                  Encryption and data security measures
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Data at Rest</span>
                      <span>100%</span>
                    </div>
                    <Progress value={100} className="h-2" />
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Data in Transit</span>
                      <span>100%</span>
                    </div>
                    <Progress value={100} className="h-2" />
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Backup Encryption</span>
                      <span>100%</span>
                    </div>
                    <Progress value={100} className="h-2" />
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Key Rotation</span>
                      <span>Current</span>
                    </div>
                    <Progress value={85} className="h-2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Alert>
            <ShieldCheck className="h-4 w-4" />
            <AlertDescription>
              All security monitoring systems are operational. Last system check completed at{' '}
              {new Date().toLocaleTimeString()}.
            </AlertDescription>
          </Alert>
        </TabsContent>
      </Tabs>
    </div>
  );
};